import * as React from 'react';

import { Typography, Stack, Box, useTheme, styled  } from '@mui/material';
import type { Theme } from '@mui/material';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { brandGradientCss } from '@repo/ui';

/**
 * The filled bar's gradient — the one this component has always drawn by default,
 * hoisted out of the styled() below so callers that pass an explicit `color` can ask
 * for THIS rather than re-typing the literal.
 *
 * Exported because the `color` prop replaces the bar background wholesale (see the
 * `color ?` branch further down), so any caller wanting the house bar while still
 * using that branch had no way to name it and would have had to copy the string. A
 * second copy is how the two drift.
 *
 * Note it is a `background`, not a colour, so it can be handed to `color` unchanged —
 * that prop is assigned straight to `background`.
 */
/*
 * The ONE brand gradient, read from `@repo/ui` rather than restated here.
 *
 * This file used to carry THREE gradient literals: this one (`#825AC3 -> #372789`, the
 * canonical pair) and two more below that ended on `#0B0A67` instead — a different purple,
 * so the same component drew two different ramps depending on which element you looked at.
 * `brandGradient.ts`'s own docblock predicted exactly this ("the moment a SECOND surface
 * wants the same gradient, those triples get copied"), and this was that moment.
 *
 * Direction is now dark-to-light, left to right, decided 2026-10-29 — see `brandGradient.ts`.
 * It changes here by reading the token, with nothing to flip locally.
 */
export const PROGRESS_BAR_GRADIENT = brandGradientCss();

/**
 * The mode-aware track every proportion bar sits on: `grey[100]` light, `grey[200]` dark.
 * Why dark takes the next step up the (inverted) dark ramp is in the `trackSx` docblock below.
 *
 * Exported for bars that are not this component but are the same object (a funnel's
 * rungs, say), so they read off one source instead of re-deriving the pair, which is how
 * the dark-mode "reads as full" bug got in the first time.
 */
export const progressTrackColor = (theme: Theme): string =>
  theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[100];

const BorderLinearProgress = styled(LinearProgress)(() => ({
  height: 10,
  borderRadius: 5,
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    background: PROGRESS_BAR_GRADIENT,
  },
}));

interface ProgressProps {
  value: number
  min: number
  max: number
  minCircle?: number | string
  maxCircle?: number | string
  showCircles?: boolean
  color?: string
  topLabels? : any
  bottomLabels? : any
  rounded?: string,
  /** Optional marker on the track at this value (must fall within [min,max]). */
  tick?: number
  /**
   * Text rendered above the track at the tick's position. Keep it to the FIGURE
   * itself — the position is what the marker contributes, and a caption repeating
   * what the surrounding card already says is the thing this replaced.
   */
  tickLabel?: string
  /**
   * Milestone circles positioned by value along the track (replaces the
   * min/max/tick circles when provided). Each stop renders a circle with `label`
   * inside and an optional `subLabel` below; `active` fills it with the brand
   * gradient (else it's a greyed "upcoming" node).
   */
  stops?: Array<{
    position: number
    label: string
    subLabel?: string
    active?: boolean
  }>
  /**
   * Suppresses the min/max (or bottomLabels) row rendered under the track.
   * VERA-1095: added so callers that don't want a numeric label row at all —
   * e.g. a password-strength meter, where the caller renders its own
   * requirement checklist below instead — don't get an unwanted blank/0-100
   * row forced in by the default behaviour.
   */
  hideLabels?: boolean
  sx?: object
}
  
export const Progress = (props: ProgressProps) => {
  const { 
    value = 0, 
    min = 0, 
    max = 100, 
    minCircle = '$35', 
    maxCircle = '$30', 
    showCircles = false,
    bottomLabels = false,
    topLabels = false,
    color,
    rounded = 'all',
    tick,
    tickLabel,
    stops,
    hideLabels = false,
    sx = {}
  } = props
  const theme = useTheme()
  /**
   * Percent along the track, CLAMPED to [0,100] and total for every input.
   *
   * The bare `((value - min) * 100) / (max - min)` this replaces had two live failure
   * modes, both reached by the tag-inventory gauge:
   *
   * 1. **Zero span.** An unset alert threshold arrives as 0 (`?? 0` on the server), and the
   *    gauge derives its bounds from it — so `min === max === 0` and the expression is
   *    `0/0` → NaN, or `n/0` → Infinity. MUI turns that into `transform: translateX(NaN%)`,
   *    which paints nothing and produces the mangled track this fixes. A zero-span scale
   *    has no meaningful position, so it reports 0 rather than inventing one.
   * 2. **Out-of-range value.** A caller that hands over a value outside [min,max] got a
   *    negative or >100 percentage. MUI does not clamp either: -4 renders as
   *    `translateX(-104%)`, i.e. the bar slides off its own track.
   *
   * Clamping HERE rather than at each call site is the point — `tick` and `stops` read the
   * same function, and `stops` had already grown its own local `Math.max(0, Math.min(100, …))`
   * (now redundant but harmless) because the shared one did not do it.
   */
  const normalise = (value) => {
    const span = max - min
    if (!Number.isFinite(span) || span <= 0) return 0
    const pct = ((Number(value) - min) * 100) / span
    if (!Number.isFinite(pct)) return 0
    return Math.min(100, Math.max(0, pct))
  };
  /**
   * The bottom-label row is rendered only when it would actually SHOW something.
   *
   * It used to render unconditionally, which cost callers real vertical space for nothing:
   * a caller suppressing its labels still had to pass a placeholder (`['', '']`), and the
   * row then reserved a full `body3` line height plus its `mt` for two empty `Typography`
   * elements. That is not cosmetic when several `Progress` sit side by side in a flex row
   * under `alignItems: 'center'` — an empty label row makes one column SHORTER than its
   * neighbours, so centring lands their tracks at different `y` and the "single" bar renders
   * as disjoint pieces at different heights. That is exactly what the tag-inventory gauge
   * did (measured: a 9px step between segments, on an 8px-tall track, so the pieces did not
   * even overlap).
   *
   * The `false` default still means "label the ends with min/max" — the two are computed
   * here so the emptiness test and the render read the same values.
   */
  const bottomLeft = typeof bottomLabels == 'object' ? bottomLabels[0] : min
  const bottomRight = typeof bottomLabels == 'object' ? bottomLabels[1] : max
  const isBlank = (label) => label === null || label === undefined || label === ''
  const hasBottomLabels = !(isBlank(bottomLeft) && isBlank(bottomRight))
  const showTick =
    typeof tick === 'number' && tick >= min && tick <= max && max > min
  const tickPct = showTick ? normalise(tick as number) : 0
  const useStops = Array.isArray(stops) && stops.length > 0
  const brandGradient = PROGRESS_BAR_GRADIENT

  /**
   * Track colour, taken from the `useTheme()` value and applied through `sx`.
   *
   * It used to live in the module-level `styled()` above, hard-wired to
   * `grey[100]`, and the result was a bar that read as FULL in dark mode: a low
   * fill was dark purple sitting on a track light enough to look like more fill.
   * Reading the colour here means it inverts with the mode like everything else
   * on the card.
   *
   * Dark takes `grey[200]` rather than `grey[100]` on purpose. `grey[100]` in
   * dark is `#27272a`, which is legible against the card but leaves the fill's
   * dark end (`#372789`) muddy against it — the two are close enough that a 90%
   * bar and a 10% bar look similar. `grey[200]` (`#3f3f46`) separates them.
   */
  /**
   * The gradient spans the FULL TRACK, not the fill.
   *
   * MUI sizes the `.bar` element to the value, and a background on it therefore SQUEEZES the
   * whole ramp into whatever width the fill happens to be — so a 17% bar showed the entire
   * light-to-dark sweep compressed into a sixth of the track, ending dark. That reads as
   * "nearly finished" at 1 of 6, which is the opposite of what a progress bar is for.
   *
   * Scaling the background to `100/pct` of the bar's own width makes the ramp resolve against
   * the track instead, so the fill reveals the first `pct`% of one continuous gradient: pale
   * at the start, deepening only as real progress is made.
   *
   * Applies to every consumer of this component, Billing's usage meter included — which wants
   * the same thing for the same reason.
   */
  const fillPct = normalise(value)
  const gradientSpansTrackSx = {
    [`& .${linearProgressClasses.bar}`]: {
      backgroundSize: `${fillPct > 0 ? (100 / fillPct) * 100 : 100}% 100%`,
      backgroundPosition: 'left center',
      backgroundRepeat: 'no-repeat',
    },
  }

  const trackSx = {
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: progressTrackColor(theme),
    },
  }

  /**
   * "You are here" — the reader's own position on a milestone track, as a bare
   * number above the bar.
   *
   * Without it the stops track draws the PRICE LADDER and nothing else, so it
   * reads as a pricing chart rather than as progress: three rates and two
   * thresholds, with no answer to "where am I on this?". The fill cannot carry
   * that on its own — at a low count it is a couple of percent of the track.
   *
   * What it is NOT, any more: a pin plus a caption. Both were dropped because
   * neither earned its space. The caption ("N used") restated a figure the card
   * already prints at 32px directly above, and the caret only existed to
   * disambiguate a marker that had been pushed below the bar and away from what
   * it pointed at. Sitting above the track, the number's own POSITION is the
   * whole message, which is the one thing the surrounding card cannot say.
   *
   * `bottom` puts it in a band of its own, clear of the 36px stop circles (which
   * reach 13px above the track) and of their threshold sub-labels above those, so
   * it can never collide with a stop whatever the count.
   *
   * The transform is `-tickPct%` of the label's OWN width, not a flat -50%. That
   * interpolates the anchor from left-aligned at 0% to right-aligned at 100%,
   * passing through centred at the midpoint — so a low count (the common case
   * here, and the one where a centred label would hang off the left edge of the
   * card) simply sits at the start of the bar, with no clipping and no
   * discontinuous jump at some arbitrary cutoff.
   */
  const tickMarker =
    showTick && tickLabel ? (
      <Box
        aria-label={`Your position: ${tickLabel}`}
        sx={{
          position: 'absolute',
          bottom: 'calc(100% + 34px)',
          left: `${tickPct}%`,
          transform: `translateX(-${tickPct}%)`,
          zIndex: 4,
          pointerEvents: 'none',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'nowrap',
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {tickLabel}
        </Typography>
      </Box>
    ) : null
  return (
    <Stack spacing={showCircles ? 2 : 0} sx={{ flexGrow: 1 }}>
      {/* TODO: add tooltips for more info */}
      {/* {labels && (
        <Stack direction="row" justifyContent="space-between">
         <Typography ml={.25} mb={1} variant="body4" color="text.secondary">
           {min}
         </Typography>
         <Typography mr={.25} mb={1} variant="body4" color="text.secondary">
           {max}
         </Typography>
       </Stack>
      )} */}
      {color ? (
        <LinearProgress variant="determinate"  value={normalise(value)} sx={{
          height: 8,
          // Mode-aware track, no border — one source, shared with the bars below.
          ...trackSx,
          borderTopLeftRadius: rounded == 'left' || rounded == 'all' ? 5 : 0,
          borderBottomLeftRadius: rounded == 'left' || rounded == 'all' ? 5 : 0,
          borderTopRightRadius: rounded == 'right' || rounded == 'all' ? 5 : 0,
          borderBottomRightRadius: rounded == 'right' || rounded == 'all' ? 5 : 0,
          [`& .${linearProgressClasses.bar}`]: {
            background: color,
            borderTopLeftRadius: rounded == 'none' ? 0 : rounded == 'left' || rounded == 'all' ? 5 : 0,
            borderBottomLeftRadius: rounded == 'none' ? 0 : rounded == 'left' || rounded == 'all' ? 5 : 0,
            borderTopRightRadius: rounded == 'none' ? 0 : rounded == 'right' || rounded == 'all' ? 5 : 0,
            borderBottomRightRadius: rounded == 'none' ? 0 : rounded == 'right' || rounded == 'all' ? 5 : 0,
          },
          ...sx
        }} />
      ): useStops ? (
        // Milestone-circle track: circles positioned by value, each filled (brand
        // gradient) once the fill reaches it, else a greyed "upcoming" node. The
        // threshold sub-labels sit ABOVE the circles so they never collide with the
        // content beneath the meter.
        //
        // PADDING on an outer wrapper, not margin on the track — and this is not a
        // preference. The parent is a MUI `Stack` with `spacing`, which emits
        // `& > :not(style):not(style) { margin: 0 }` at a higher specificity than a
        // child's own `sx`, so the `mt: 48px / mb: 28px` that used to live on the
        // track below computed to 0px and always had. Nothing looked broken only
        // because the overflow is visible: the threshold sub-labels rode up into
        // the chart above and the circles' lower halves sat on top of whatever came
        // next. Padding is not zeroed by that rule, so it actually reserves the
        // space. It goes on a WRAPPER because padding on the positioned element
        // would move the origin that `top: 50%` and `100%` resolve against, which
        // is what places every circle.
        <Box
          sx={{
            // 48px reserves the circles' upper half and their threshold
            // sub-labels; the marker sits in a band above those, so it needs its
            // own room on top rather than sharing theirs.
            pt: tickMarker ? '62px' : '48px',
            // The circles' lower half. Nothing hangs below the track any more.
            pb: '28px',
          }}
        >
        <Box sx={{ position: 'relative' }}>
          <BorderLinearProgress variant="determinate" value={value} sx={{ ...trackSx, ...gradientSpansTrackSx }} />
          {tickMarker}
          {stops!.map((stop, index) => {
            const pct = Math.max(0, Math.min(100, normalise(stop.position)))
            // Keep edge circles inside the track bounds instead of half-hanging.
            const shiftX = pct <= 0 ? '0%' : pct >= 100 ? '-100%' : '-50%'
            return (
              <Box
                key={`${stop.label}-${index}`}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: `${pct}%`,
                  transform: `translate(${shiftX}, -50%)`,
                  height: 36,
                  width: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  zIndex: 3,
                  ...(stop.active
                    ? { color: theme.palette.common.white, background: brandGradient, border: 'none' }
                    : {
                        // Full-contrast label so the rate reads clearly on the grey
                        // "upcoming" node (text.secondary was too faint).
                        color: theme.palette.text.primary,
                        background: theme.palette.grey[200],
                        border: `1px solid ${theme.palette.white[100]}`,
                      }),
                }}
              >
                {stop.label}
                {stop.subLabel && (
                  <Typography
                    variant="body4"
                    sx={{
                      position: 'absolute',
                      bottom: 'calc(100% + 6px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      whiteSpace: 'nowrap',
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {stop.subLabel}
                  </Typography>
                )}
              </Box>
            )
          })}
        </Box>
        </Box>
      ) : (
        <Box sx={{ position: 'relative' }}>
          <BorderLinearProgress variant="determinate" value={value} sx={{ ...trackSx, ...gradientSpansTrackSx }} />
          {showTick && (
            <>
              {/* The position marker itself — a thin line at the tick's x — is fine
                  centred on its own 2px box; only the LABEL needs edge-awareness. */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -3,
                  bottom: -3,
                  left: `${tickPct}%`,
                  width: '2px',
                  transform: 'translateX(-1px)',
                  borderRadius: 1,
                  backgroundColor: theme.palette.common.white,
                  zIndex: 2,
                }}
              />
              {/*
                A SIBLING of the line, anchored to the TRACK's own width — not
                nested inside the 2px line and centred on it.

                The label used to be a child of that 2px box with a flat
                `translateX(-50%)`, which centres it on the tick regardless of
                where the tick sits. At `tick === min` (e.g. a brand-new company
                at 0 activations, the common first-run state) that puts the
                label's CENTRE on the track's left edge, so roughly half its
                width renders to the left of the bar — outside it, reading as
                "detached" rather than as a label for the start.

                This mirrors the milestone-stops tickMarker below (and its own
                docblock): interpolate the anchor from left-aligned at 0% to
                right-aligned at 100%, passing through centred at the midpoint,
                so the label is flush against whichever edge it sits at and never
                overhangs the track.
              */}
              {tickLabel && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 'calc(100% + 4px)',
                    left: `${tickPct}%`,
                    transform: `translateX(-${tickPct}%)`,
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}
                >
                  <Typography
                    variant="body4"
                    sx={{
                      whiteSpace: 'nowrap',
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {tickLabel}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
      {/*
        Bottom-label TEXT and the min/max CIRCLES both describe the same two
        endpoints, so they must be mutually exclusive — never rendered together.

        Before this, a caller passing both `bottomLabels` and `showCircles`
        (the billing activation meter does exactly that) got BOTH: the plain
        text row directly under the track, AND the circle row layered on top of
        it via a hardcoded `marginTop: -68px` tuned for "immediately below an
        otherwise-empty track" — not for "below a labels row that's also
        there." Whenever the milestone-stops path isn't in play (unbounded top
        band, or no included allotment to plot — both real, reachable states),
        the two collided: the circles' negative margin pulled them up over the
        text row instead of over empty space, so the endpoint numbers read as
        floating/overlapping rather than sitting cleanly under the bar.

        `showCircles` is the caller's explicit choice of the richer treatment,
        so it wins; the plain text row only renders when circles are not
        requested.

        THREE gates, not two — this line was a merge conflict and the resolution
        is the conjunction, not a choice between sides. Two branches each added
        one gate for an unrelated caller: `!showCircles` for the billing
        activation meter (above), `!hideLabels` for the auth password meter,
        which passes the opt-out explicitly. `hasBottomLabels` is the third and
        oldest — whether there is any label content at all. Dropping any one
        re-opens that one's bug, and they are independent props (37/80 and
        69/88), so no gate implies another.
      */}
      {!useStops && !showCircles && !hideLabels && hasBottomLabels && (
        <Stack direction="row" justifyContent="space-between">
          <Typography ml={.25} mt={1} variant="body3" color="text.secondary">
            {bottomLeft}
          </Typography>
          <Typography mr={.25} mt={1} variant="body3" color="text.secondary">
          {bottomRight}
          </Typography>
        </Stack>
      )}
      {showCircles && !useStops && (
        <Stack direction="row" justifyContent="space-between">
          <Box sx={{
            height: 36,
            width: 36,
            borderRadius: 45,
            // Purple gradient node: only the FONT is white (theme.palette.white[100]
            // flips to near-black in dark mode), no white ring — the node stands on
            // its own gradient.
            border: 'none',
            color: theme.palette.common.white,
            fontWeight: 700,
            marginTop: '-68px',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 3,
            p: 1,
            fontSize: 14,
            background: PROGRESS_BAR_GRADIENT,
          }}>
            {minCircle}
          </Box>
          <Box sx={{
            height: 36,
            width: 36,
            borderRadius: 45,
            border: `1px solid ${theme.palette.white[100]}`,
            fontWeight: 700,
            marginTop: '-68px',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 3,
            p: 1,
            fontSize: 14,
            background: theme.palette.grey[200],
          }}>
            {maxCircle}
          </Box>
        </Stack>
      )}
    </Stack>
  )
}